import moment from 'moment';
import { WeeklySummary } from '@amzn/aet-summaries-kit';
import { useMemo } from 'react';

/**
 * Maps summaries to weeks, creating empty states for weeks without summaries
 */
export const mapSummariesToWeeks = (
  weeks: moment.Moment[],
  summaries: WeeklySummary[]
): Array<{ week: moment.Moment; summary?: WeeklySummary }> => {
  return weeks
    .map(week => ({
      week,
      summary: summaries.find(summary =>
        moment(summary.date).isSame(week, 'day')
      ),
    }))
    .sort((a, b) => b.week.valueOf() - a.week.valueOf());
};

/**
 * Generates a range of weeks between start and end dates (inclusive)
 */
export const generateWeekRange = (
  startWeek: moment.Moment,
  endWeek: moment.Moment
): moment.Moment[] => {
  const weeks: moment.Moment[] = [];
  const current = startWeek.clone().startOf('week');
  const end = endWeek.clone().startOf('week');

  while (current.isSameOrBefore(end, 'week')) {
    weeks.push(current.clone());
    current.add(1, 'week');
  }

  return weeks;
};

/**
 * Gets the most recent Sunday (Amazon week start)
 * @param today - optional date to use as the current date, for testing help
 */
export const getLastSunday = (today?: Date): moment.Moment => {
  const mostRecentSunday = moment(today).day(0);

  // we want to show summaries from last week. so if it's sunday, set the last sunday as the previous sunday
  if (mostRecentSunday.isSame(moment(today), 'day'))
    return mostRecentSunday.clone().subtract(1, 'week');

  return mostRecentSunday;
};

/**
 * We have aligned with SSA to ensure that summary.date is always the ending Sunday the week the summaries are generated
 * note, even if they are delayed on generating the summaries, it will still only include contacts from the prev Sunday (12am-Sat:11:59.59) of the prev week,
 * and the 'summary-date' will still be the sunday
 */
export const useGetIsSummaryFromLastWeek = (
  summary?: WeeklySummary,
  today?: Date
) => {
  const lastSunday = useMemo(() => getLastSunday(today), [today]);

  return useMemo(
    () => summary && moment(summary.date).isSame(lastSunday, 'day'),
    [lastSunday, summary]
  );
};
